import React from 'react';
import { User, Calendar, Info, AlertCircle, AlertTriangle, Zap } from 'lucide-react';

interface CustomLineTooltipProps {
  x: number;
  y: number;
  visible: boolean;
  lineData: {
    label?: string;
    username?: string;
    dateAdded?: string;
    editedAt?: string;
    editedBy?: string;
    groupId?: string;
    mediaType?: 'image' | 'video';
    mediaUrl?: string;
    thumbnailUrl?: string;
    title?: string;
    description?: string;
    source?: string;
    capturedData?: {
      xValue?: any;
      yValues?: Record<string, number>;
      timestamp?: any;
    };
    severity?: 'info' | 'low' | 'medium' | 'high' | 'critical';
  } | null;
}

export const CustomLineTooltip: React.FC<CustomLineTooltipProps> = ({
  x,
  y,
  visible,
  lineData
}) => {
  if (!visible || !lineData) return null;

  // Helper function to format X value based on type
  const formatXValue = (xValue: any): string => {
    if (!xValue) return 'N/A';
    
    // Check if it's a date
    const date = new Date(xValue);
    if (!isNaN(date.getTime()) && (typeof xValue === 'string' || xValue instanceof Date)) {
      return date.toLocaleString();
    }
    
    // Check if it's a number
    if (typeof xValue === 'number') {
      return xValue.toFixed(2);
    }
    
    // Return as string
    return String(xValue);
  };

  // Helper function to get severity icon and color
  const getSeverityDisplay = (severity?: string) => {
    switch (severity) {
      case 'critical':
        return { icon: <Zap className="w-3 h-3" />, color: 'text-red-500', label: 'Critical' };
      case 'high':
        return { icon: <AlertTriangle className="w-3 h-3" />, color: 'text-orange-500', label: 'High' };
      case 'medium':
        return { icon: <AlertTriangle className="w-3 h-3" />, color: 'text-yellow-500', label: 'Medium' };
      case 'low':
        return { icon: <AlertCircle className="w-3 h-3" />, color: 'text-green-500', label: 'Low' };
      case 'info':
      default:
        return { icon: <Info className="w-3 h-3" />, color: 'text-blue-500', label: 'Info' };
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: x + 15,
        top: y,
        zIndex: 10001,
        pointerEvents: 'none',
      }}
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 min-w-[200px] max-w-[280px]"
    >
      {/* Title with severity in top right */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">{lineData.title?.split(' ')[0] || '📍'}</span>
          <span className="font-semibold text-sm text-gray-900 dark:text-white">
            {lineData.title?.replace(lineData.title.split(' ')[0], '').trim() || lineData.label || 'Event'}
          </span>
        </div>
        
        {/* Severity badge in top right corner */}
        {lineData.severity && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ml-2 ${
            lineData.severity === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
            lineData.severity === 'high' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' :
            lineData.severity === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
            lineData.severity === 'low' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
            'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
          }`}>
            {getSeverityDisplay(lineData.severity).icon}
            <span>{getSeverityDisplay(lineData.severity).label}</span>
          </div>
        )}
      </div>
      
      {/* Description if available */}
      {lineData.description && (
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
          {lineData.description}
        </p>
      )}
      
      {/* Username and date in compact format */}
      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
        {lineData.username && (
          <div className="flex items-center gap-1.5">
            <User className="w-3 h-3" />
            <span>{lineData.username}</span>
          </div>
        )}
        
        {lineData.dateAdded && (
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />
            <span className="text-xs">Added: {lineData.dateAdded}</span>
          </div>
        )}
        
        {/* Show edit history if available */}
        {lineData.editedAt && lineData.editedBy && (
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />
            <span className="text-xs">Edited: {lineData.editedAt} by {lineData.editedBy}</span>
          </div>
        )}
      </div>

      {/* Captured Data Values */}
      {lineData.capturedData && (
        <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs">
          <div className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Data Values</div>
          <div className="space-y-1">
            {lineData.capturedData.xValue && (
              <div>
                <span className="text-gray-600 dark:text-gray-400">X: </span>
                <span className="font-mono">{formatXValue(lineData.capturedData.xValue)}</span>
              </div>
            )}
            {lineData.capturedData.yValues && Object.keys(lineData.capturedData.yValues).length > 0 && (
              <div>
                <span className="text-gray-600 dark:text-gray-400">Y Values: </span>
                <div className="ml-2">
                  {Object.entries(lineData.capturedData.yValues).map(([sensor, value]) => (
                    <div key={sensor}>
                      <span className="text-gray-500">{sensor}: </span>
                      <span className="font-mono">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Media preview if available */}
      {(lineData.mediaUrl || lineData.thumbnailUrl) && (
        <div className="mt-2">
          {lineData.mediaType === 'video' ? (
            <video 
              autoPlay 
              muted 
              loop 
              className="w-full h-[140px] object-cover rounded"
              src={lineData.mediaUrl}
            />
          ) : (
            <img 
              src={lineData.thumbnailUrl || lineData.mediaUrl} 
              alt=""
              className="w-full h-[120px] object-cover rounded"
            />
          )}
        </div>
      )}
    </div>
  );
};